import type { TRefElementInstance } from 'widgets/toolbox/RefElement';

import { isBase64Encoded } from 'core/utils';

/**
 * @description Base AvailabilityReminderMixin implementation
 * @param CoreProductDetail Base widget for extending
 * @returns Availability reminder mixin widget
 */
function AvailabilityReminderMixinClassCreator(CoreProductDetail: import('core/product/CoreProductDetail').TCoreProductDetail) {
    class AvailabilityReminderMixin extends CoreProductDetail {
        /**
         * @description Handles set reminder click
         * @param button Button ref element instance
         */
        handleSetReminderClick(button: TRefElementInstance) {
            let modalUrl = button.data('modalUrl') as string;
            const modalTitle = button.data('modalTitle') as string;
            const modalSuccessTitle = button.data('modalSuccessTitle') as string;
            let signInModalUrl = button.data('signInModalUrl') as string;
            const signInModalTitle = button.data('signInModalTitle') as string;
            const shouldSignInToSubscribe = button.data('shouldSignInToSubscribe') as boolean;

            if (isBase64Encoded(modalUrl)) {
                modalUrl = atob(modalUrl);
            }

            if (isBase64Encoded(signInModalUrl)) {
                signInModalUrl = atob(signInModalUrl);
            }

            const analytics = this.currentGtmInfo || this.data('analytics');

            this.ref('html').addClass('m-active_variation');

            this.eventBus().emit('availabilityReminderModal.show', {
                url: this.getFullModalUrl(modalUrl),
                headerText: modalTitle,
                modalSuccessTitle: modalSuccessTitle,
                signInModalUrl: signInModalUrl,
                signInModalTitle: signInModalTitle,
                shouldSignInToSubscribe: shouldSignInToSubscribe,
                analytics: {
                    event_name: 'open_availability_reminder',
                    open_availability_reminder: {
                        feature: 'availability_reminder',
                        item_id: analytics?.item_id || '',
                        item_variant: this.prefs().currentProductId || ''
                    }
                }
            });
        }

        /**
         * @description Add product ID to modal URL
         * @param modalUrl Modal URL to be modified
         */
        getFullModalUrl(modalUrl: string): string {
            const productID = this.prefs().currentProductId || this.prefs().pid;

            if (!productID) {
                // eslint-disable-next-line no-console
                console.error('AvailabilityReminder: Product ID is missing. Cannot generate availability reminder modal URL.');

                return '';
            }

            const url = new URL(modalUrl);

            url.searchParams.set('pid', productID);

            return url.toString();
        }
    }

    return AvailabilityReminderMixin;
}

export type TAvailabilityReminderMixin = ReturnType<typeof AvailabilityReminderMixinClassCreator>;

export type TAvailabilityReminderMixinInstance = InstanceType<TAvailabilityReminderMixin>;

export default AvailabilityReminderMixinClassCreator;
